import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { observable } from "mobx";
import { FormGroup, Button, Intent, TextArea } from "@blueprintjs/core";
import Toast from "helpers/toaster";
import FormDrawer from "components/FormDrawer";
import { FormSection, FieldError } from "components/FormBits";

const blank = (value) => value === "" || value === null || value === undefined;

// Field -> input id, in the order they appear (the first with a problem gets focus).
const FIELD_IDS = {
  name: "new-supplier-name",
  phone: "new-supplier-phone",
  email: "new-supplier-email",
  address: "new-supplier-address",
};

const focusField = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.focus();
    element.scrollIntoView({ block: "center" });
  }
};

@inject("supplierStore")
@observer
class AddSupplierModal extends Component {
  @observable errors = {};

  // Cancelling clears the form too: the inputs come back empty on the next
  // open, so the store must not keep values the user can't see.
  handleCloseSupplierDailog() {
    this.props.supplierStore.clearSupplierObject();
    this.errors = {};
    this.props.onClose();
  }

  setField(field, value) {
    this.props.supplierStore.newSupplier.object[field] = value;
    if (this.errors[field]) {
      this.errors = { ...this.errors, [field]: null };
    }
  }

  createSupplier() {
    let {
      props: {
        supplierStore: {
          newSupplier: {
            object
          }
        }
      }
    } = this;

    const errors = {};
    if (blank(object.name)) errors.name = "Enter the supplier's name.";
    if (blank(object.phone)) errors.phone = "Enter a phone number.";
    if (blank(object.email)) errors.email = "Enter an email address.";
    if (blank(object.address)) errors.address = "Enter the supplier's address.";
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((field) => errors[field]);
    if (first) {
      focusField(FIELD_IDS[first]);
      return;
    }

    this.props.supplierStore
      .createSupplier()
      .then(this.onSuccessfulAddSupplier.bind(this))
      .catch(this.onFailAddSupplier.bind(this));
  }

  onSuccessfulAddSupplier(response) {
    this.props.onSuccess(response.data.data);
  }

  onFailAddSupplier(error) {
    Toast.show({
      icon: "warning-sign",
      message: error.response.data.errors.join(", "),
      intent: Intent.DANGER
    });
    console.log(error);
  }

  field(field, label, input) {
    const id = FIELD_IDS[field];
    const error = this.errors[field];
    return (
      <FormGroup
        label={label}
        labelFor={id}
        labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
        className={error ? "has-error" : undefined}
      >
        {React.cloneElement(input, {
          id,
          "aria-required": true,
          "aria-invalid": error ? true : undefined,
          "aria-describedby": error ? `${id}-error` : undefined,
        })}
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </FormGroup>
    );
  }

  render() {
    let {
      supplierStore: {
        newSupplier: {
          object = {},
          isLoading
        }
      }
    } = this.props;
    const close = this.handleCloseSupplierDailog.bind(this);
    return (
      <FormDrawer
        size="min(760px, 100vw)"
        isOpen={this.props.isOpen}
        title="New supplier"
        icon="office"
        onClose={close}
        busy={isLoading}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={isLoading} />
            <Button
              intent={Intent.PRIMARY}
              icon="add"
              text="Create supplier"
              loading={isLoading}
              onClick={this.createSupplier.bind(this)}
            />
          </>
        }
      >
        <FormSection title="Supplier" hint="Who you buy parts and materials from, and how to reach them.">
          {this.field(
            "name",
            "Name",
            <input
              type="text"
              className="bp3-input bp3-fill"
              placeholder="e.g. Ferguson Plumbing Supply"
              autoComplete="off"
              value={object.name || ""}
              onChange={(event) => this.setField("name", event.target.value)}
            />
          )}
          <div className="row">
            <div className="col-6">
              {this.field(
                "phone",
                "Phone",
                <input
                  type="tel"
                  inputMode="tel"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. (555) 123-4567"
                  autoComplete="off"
                  value={object.phone || ""}
                  onChange={(event) => this.setField("phone", event.target.value)}
                />
              )}
            </div>
            <div className="col-6">
              {this.field(
                "email",
                "Email",
                <input
                  type="email"
                  className="bp3-input bp3-fill"
                  placeholder="orders@example.com"
                  autoComplete="off"
                  value={object.email || ""}
                  onChange={(event) => this.setField("email", event.target.value)}
                />
              )}
            </div>
          </div>
        </FormSection>

        <FormSection title="Address" hint="Where you pick up orders or send payment.">
          {this.field(
            "address",
            "Address",
            <TextArea
              className="bp3-fill"
              placeholder={"e.g. 400 Industrial Pkwy\nSpringfield, IL 62702"}
              rows={3}
              value={object.address || ""}
              onChange={(event) => this.setField("address", event.target.value)}
            />
          )}
        </FormSection>
      </FormDrawer>
    );
  }
}

export default AddSupplierModal;
